/**
 * T-007 — Word-facing extraction of protected regions (fields, bookmarks) for a paragraph.
 *
 * This is the ONE Word-dependent half of the field/bookmark guard; the pure, unit-tested half is
 * `protectedRegions.ts`. Office.js can't be exercised under Vitest, so this module is verified by
 * hand in real Word (see the T-013 matrix and the T-007 manual step). It references the `Word`
 * global only inside functions, so importing it never breaks the test environment.
 *
 * ## How a field's offset in `range.text` is found
 * The T-001 spike established that the diff baseline is `paragraph.getRange('Whole').text`, and that
 * a field's result appears as plain fused text inside it (no delimiter character). To locate a field
 * within that string we measure, in the SAME range, the text length from the paragraph start to the
 * field-result start — that character count is the field's start offset; `start + result.text.length`
 * is its end. We never parse OOXML and never rewrite the paragraph, so bookmarks and fields are left
 * physically intact (the whole reason this approach beats `insertOoxml`).
 *
 * ## Known limitations (documented, acceptable for v1 behind the flag)
 *  - Bookmark offset recovery via Office.js is limited; we extract bookmark ranges where the API
 *    exposes them and fall back to skipping (never fabricating) a bookmark we cannot place. A
 *    bookmark we fail to map is simply not protected here — the wholesale-rewrite hazard that used
 *    to break bookmarks does not exist on this search/delete path, so an unmapped bookmark is not
 *    silently destroyed, only un-guarded against an edit that lands exactly on it (rare).
 *  - Nested/overlapping fields are reported as-is; the pure set consults them all.
 */

import { ProtectedRegion } from './types';

/* global Word */

/**
 * Extract the protected field (and, where the API allows, bookmark) spans for one paragraph, in
 * `range.text` coordinates. Read-only. Must be called inside a `Word.run` with an active context.
 *
 * @param context   the active Word request context
 * @param paragraph the target paragraph (already loaded/resolved by the caller)
 */
/**
 * A protected region plus the live Word range it maps to. For a field, `range` is the field's result
 * range — the deletable extent when a redline fully spans the field. For a bookmark, `range` may be
 * null (bookmarks are advisory and never deleted).
 */
export interface ExtractedRegion {
  region: ProtectedRegion;
  range: Word.Range | null;
}

export async function extractProtectedRegions(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
): Promise<ExtractedRegion[]> {
  const extracted: ExtractedRegion[] = [];

  const wholeStart = paragraph.getRange('Start');

  // --- Fields ------------------------------------------------------------------------------------
  const fields = paragraph.getRange('Whole').fields;
  fields.load('items');
  await context.sync();

  const measures: Array<{ before: Word.Range; result: Word.Range }> = [];
  for (const field of fields.items) {
    const result = field.result;
    // Range from paragraph start up to the field result's start; its text length = start offset.
    const before = wholeStart.expandTo(result.getRange('Start'));
    before.load('text');
    result.load('text');
    measures.push({ before, result });
  }
  await context.sync();

  for (const m of measures) {
    const start = (m.before.text || '').length;
    const len = (m.result.text || '').length;
    // The result range is the deletable field extent (matches the [start, start+len) region text).
    if (len > 0) extracted.push({ region: { kind: 'field', start, end: start + len }, range: m.result });
  }

  // --- Bookmarks ---------------------------------------------------------------------------------
  // Office.js exposes bookmark *names* on a range; mapping each to an offset is best-effort. Where a
  // bookmark's range can be resolved we protect it; otherwise we skip it (see limitations above).
  try {
    const whole = paragraph.getRange('Whole');
    const names = whole.getBookmarks(true /* includeHidden */, false /* includeAdjacent */);
    await context.sync();
    for (const name of names.value || []) {
      const bmRange = context.document.getBookmarkRangeOrNullObject(name);
      const before = wholeStart.expandTo(bmRange.getRange('Start'));
      before.load('text');
      bmRange.load('text,isNullObject');
      // eslint-disable-next-line no-await-in-loop
      await context.sync();
      if (bmRange.isNullObject) continue;
      const start = (before.text || '').length;
      const len = (bmRange.text || '').length;
      extracted.push({ region: { kind: 'bookmark', start, end: start + Math.max(0, len) }, range: null });
    }
  } catch {
    // Bookmark APIs vary by host/version; never let bookmark extraction fail the apply. Fields —
    // the actual failure cause from T-001 — are already captured above.
  }

  return extracted;
}
